/* ==========================================================================
   FACTQRY — <fq-card-roll> : logique de "roulement" de la carte collection.

   Réécriture vanilla (sans React/Babel) de la classe DCLogic du bundle
   Claude Design. Progressive enhancement : sans JS, les produits restent de
   simples liens <a> empilés (le premier est cliquable). Avec JS, comportement
   identique ordinateur ET mobile :
     - 1er clic sur un produit de la file → il passe en tête (grand, à gauche),
       les autres reculent dans l'ordre.
     - clic sur le produit de tête → navigation vers sa page produit.
   ========================================================================== */

if (!customElements.get('fq-card-roll')) {
  class FqCardRoll extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;

      this.tiles = Array.from(this.querySelectorAll('.fq-tile'));
      this.n = this.tiles.length;
      if (this.n === 0) return;

      // order[position] = élément tuile ; départ dans l'ordre du DOM.
      this.order = this.tiles.slice();

      this._applyPositions();
      this._bind();
    }

    _applyPositions() {
      this.order.forEach((tile, pos) => {
        for (let i = 0; i < this.n; i++) tile.classList.remove('is-pos-' + i);
        tile.classList.add('is-pos-' + pos);
      });
    }

    _posOf(tile) {
      return this.order.indexOf(tile);
    }

    _bringToFront(pos) {
      if (pos <= 0) return;
      const n = this.order.length;
      const next = [];
      for (let k = 0; k < n; k++) next.push(this.order[(pos + k) % n]);
      this.order = next;
      this._applyPositions();
    }

    _bind() {
      this.tiles.forEach((tile) => {
        tile.addEventListener('click', (e) => {
          const pos = this._posOf(tile);
          if (pos === 0) return; // tuile de tête → on laisse le lien naviguer
          e.preventDefault(); // tuile de file → 1er clic : elle passe devant
          this._bringToFront(pos);
        });
      });
    }
  }

  customElements.define('fq-card-roll', FqCardRoll);
}

/* ==========================================================================
   FACTQRY — <fq-cards-carousel> : contrôles du défilement horizontal mobile.
   Raisonne par COLONNE (une "page" = les cartes qui partagent la même
   position horizontale — sur mobile, 2 cartes empilées verticalement).
   Gère les flèches, un point par page, et l'état au scroll.
   Sur desktop (grille), la barre de nav est masquée par le CSS.
   ========================================================================== */
if (!customElements.get('fq-cards-carousel')) {
  class FqCardsCarousel extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;

      this.scroller = this.querySelector('[data-scroller]');
      this.nav = this.querySelector('[data-nav]');
      this.dotsWrap = this.querySelector('[data-dots]');
      this.prevBtn = this.querySelector('[data-prev]');
      this.nextBtn = this.querySelector('[data-next]');
      if (!this.scroller) return;

      this.cards = Array.from(this.scroller.children);
      this.dots = [];
      this._bind();
      this._update();
    }

    // Première carte de chaque colonne (offsetLeft distinct) = une page.
    _colFirsts() {
      const firsts = [];
      let last = -Infinity;
      this.cards.forEach((c) => {
        if (c.offsetLeft > last + 2) {
          firsts.push(c);
          last = c.offsetLeft;
        }
      });
      return firsts;
    }

    _index(cols) {
      const base = this.scroller.getBoundingClientRect().left;
      let best = 0;
      let bd = Infinity;
      cols.forEach((c, i) => {
        const d = Math.abs(c.getBoundingClientRect().left - base);
        if (d < bd) {
          bd = d;
          best = i;
        }
      });
      return best;
    }

    _scrollToIndex(i) {
      const cols = this._colFirsts();
      if (!cols.length) return;
      const clamped = Math.max(0, Math.min(cols.length - 1, i));
      const left =
        this.scroller.scrollLeft +
        (cols[clamped].getBoundingClientRect().left -
          this.scroller.getBoundingClientRect().left);
      this.scroller.scrollTo({ left: left, behavior: 'smooth' });
    }

    _syncDots(count) {
      if (!this.dotsWrap || this.dots.length === count) return;
      this.dotsWrap.innerHTML = '';
      this.dots = [];
      for (let i = 0; i < count; i++) {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'fq-cardroll-demo__dot';
        b.setAttribute('aria-label', ((window.fqStrings && window.fqStrings.rollGoToPage) || 'Aller à la page [page]').replace('[page]', i + 1));
        b.addEventListener('click', () => this._scrollToIndex(i));
        this.dotsWrap.appendChild(b);
        this.dots.push(b);
      }
    }

    _bind() {
      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', () =>
          this._scrollToIndex(this._index(this._colFirsts()) - 1)
        );
      }
      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', () =>
          this._scrollToIndex(this._index(this._colFirsts()) + 1)
        );
      }
      let raf;
      this.scroller.addEventListener(
        'scroll',
        () => {
          cancelAnimationFrame(raf);
          raf = requestAnimationFrame(() => this._update());
        },
        { passive: true }
      );
      window.addEventListener('resize', () => this._update());
    }

    _update() {
      const s = this.scroller;
      const scrollable = s.scrollWidth > s.clientWidth + 4;
      if (this.nav) this.nav.classList.toggle('is-hidden', !scrollable);
      if (!scrollable) return;

      const cols = this._colFirsts();
      this._syncDots(cols.length);

      const atStart = s.scrollLeft <= 2;
      const atEnd = s.scrollLeft + s.clientWidth >= s.scrollWidth - 2;
      if (this.prevBtn) this.prevBtn.disabled = atStart;
      if (this.nextBtn) this.nextBtn.disabled = atEnd;

      const active = this._index(cols);
      this.dots.forEach((d, i) => d.classList.toggle('is-active', i === active));
    }
  }

  customElements.define('fq-cards-carousel', FqCardsCarousel);
}
